/**
 * Utility functions for ScannerConfigurationForm
 */
import {
  AUTH_METHOD_FIELDS,
  AUTH_FIELD_DEFAULTS,
  BASE_CONNECTION_FIELDS,
  DAY_NAME_MAP,
  DEFAULT_FORM_VALUES
} from './constants';

/**
 * Helper function to resolve a value from multiple possible paths in the data object.
 * This abstracts the repetitive property lookup logic (DRY principle).
 *
 * @param {Object} data - The source data object
 * @param {string} field - The field name to look for
 * @param {*} defaultValue - The default value if not found
 * @param {string[]} paths - Nested paths to check (e.g., ['parameters', 'config'])
 * @returns {*} The resolved value or default
 */
export const resolveField = (
  data,
  field,
  defaultValue = '',
  paths = ['parameters', 'config']
) => {
  if (!data) return defaultValue;

  // Check direct field first
  if (data[field] !== undefined && data[field] !== null) {
    return data[field];
  }

  // Check nested paths in order of priority
  for (const path of paths) {
    const nestedValue = data[path]?.[field];

    if (nestedValue !== undefined && nestedValue !== null) {
      return nestedValue;
    }
  }

  return defaultValue;
};

/**
 * Helper function to get auth-specific fields based on the authentication method.
 * Only initializes fields relevant to the selected auth method.
 *
 * @param {Object} initialData - The source data object
 * @param {string} authMethod - The authentication method
 * @returns {Object} Auth-specific fields with their values
 */
export const getAuthSpecificFields = (initialData, authMethod) => {
  const fieldDefaults = AUTH_FIELD_DEFAULTS[authMethod];

  if (!fieldDefaults) {
    return {};
  }

  return Object.entries(fieldDefaults).reduce((acc, [field, defaultValue]) => {
    acc[field] = resolveField(initialData, field, defaultValue);

    return acc;
  }, {});
};

/**
 * Helper function to get all connection-related fields
 * @param {string} authMethod - The authentication method
 * @returns {string[]} Array of connection field names
 */
export const getConnectionFields = (authMethod) => {
  const authSpecificFields = AUTH_METHOD_FIELDS[authMethod] || [];

  return [...BASE_CONNECTION_FIELDS, ...authSpecificFields];
};

/**
 * Helper function to check if a field is connection-related
 * @param {string} field - The field name to check
 * @param {string} authMethod - The authentication method
 * @returns {boolean} True if the field is connection-related
 */
export const isConnectionField = (field, authMethod) => {
  const connectionFields = getConnectionFields(authMethod);

  return connectionFields.includes(field);
};

/**
 * Helper function to get current day of week
 * @returns {string} Current day name (e.g., 'Monday')
 */
export const getCurrentDayOfWeek = () => {
  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  const today = new Date();

  return days[today.getDay()];
};

/**
 * Get ordinal suffix for a day number
 * @param {number} day - Day number (1-31)
 * @returns {string} Ordinal suffix ('st', 'nd', 'rd', 'th')
 */
export const getOrdinalSuffix = (day) => {
  const suffixes = ['st', 'nd', 'rd'];

  if (day <= 3) {
    return suffixes[day - 1];
  }

  if (day === 21) return 'st';
  if (day === 22) return 'nd';
  if (day === 23) return 'rd';

  return 'th';
};

/**
 * Helper function to get current day of month with ordinal suffix
 * @returns {string} Current day with suffix (e.g., '4th')
 */
export const getCurrentDayOfMonth = () => {
  const today = new Date();
  const day = today.getDate();

  return `${day}${getOrdinalSuffix(day)}`;
};

/**
 * Helper function to initialize form data from props.
 * Only initializes fields relevant to the specific authentication method,
 * avoiding "ghost data" and keeping the state clean.
 *
 * @param {Object} initialData - The initial data object from props
 * @returns {Object} Clean form data with only relevant fields
 */
export const initializeFormData = (initialData) => {
  // Determine the auth method first (check aliased fields: method vs authMethod)
  const authMethod =
    resolveField(initialData, 'method', '') ||
    resolveField(initialData, 'authMethod', '', []);

  // Common fields that are always needed regardless of auth method
  const commonFields = {
    id: initialData?.id || undefined,
    name: initialData?.name || '',
    description: initialData?.description || '',
    // targetSystem has aliases: target vs targetSystem
    targetSystem:
      resolveField(initialData, 'target', '') ||
      resolveField(initialData, 'targetSystem', '', []),
    authMethod,
    // Run policy fields
    runFrequency: resolveField(
      initialData,
      'runFrequency',
      DEFAULT_FORM_VALUES.runFrequency,
      ['runPolicy', 'advancedSettings']
    ),
    runTime: resolveField(initialData, 'runTime', DEFAULT_FORM_VALUES.runTime, [
      'runPolicy',
      'advancedSettings'
    ]),
    syncReview: resolveField(
      initialData,
      'syncReview',
      DEFAULT_FORM_VALUES.syncReview,
      ['runPolicy', 'advancedSettings']
    ),
    // Advanced settings
    emailNotificationsEnabled: resolveField(
      initialData,
      'emailNotificationsEnabled',
      DEFAULT_FORM_VALUES.emailNotificationsEnabled,
      ['advancedSettings']
    ),
    recipientEmails: resolveField(
      initialData,
      'recipientEmails',
      DEFAULT_FORM_VALUES.recipientEmails,
      ['advancedSettings']
    )
  };

  // Get only the auth-specific fields relevant to the selected authMethod
  const specificFields = getAuthSpecificFields(initialData, authMethod);

  // Merge common fields with auth-specific fields
  return {
    ...commonFields,
    ...specificFields
  };
};

/**
 * Generate a schedule label from custom schedule settings
 * @param {Object} customSchedule - Custom schedule configuration
 * @returns {string} Human-readable schedule label
 */
export const generateScheduleLabel = (customSchedule) => {
  const { intervalNumber, intervalUnit, repeatDays, monthDay } = customSchedule;

  if (intervalUnit === 'weeks') {
    const selectedDay = repeatDays[0] || 'Monday';
    const dayName = DAY_NAME_MAP[selectedDay] || selectedDay;

    if (intervalNumber === 1) {
      return `Weekly on ${dayName}`;
    }

    return `Every ${intervalNumber} weeks on ${dayName}`;
  }

  if (intervalUnit === 'months') {
    return `Monthly on the ${monthDay}${getOrdinalSuffix(monthDay)}`;
  }

  if (intervalNumber === 1) {
    return 'Daily';
  }

  return `Every ${intervalNumber} days`;
};

/**
 * Generate month day options for custom schedule
 * @returns {Array<{label: string, value: string}>} Month day options
 */
export const generateMonthDayOptions = () =>
  Array.from({ length: 30 }, (_, i) => {
    const day = i + 1;

    return {
      label: `${day}${getOrdinalSuffix(day)}`,
      value: `${day}`
    };
  });

/**
 * Generate interval number options based on interval unit
 * @param {string} intervalUnit - The interval unit ('days', 'weeks', 'months')
 * @returns {Array<{label: string, value: string}>} Interval number options
 */
export const generateIntervalNumberOptions = (intervalUnit) => {
  if (intervalUnit === 'months') {
    return [{ label: '1', value: '1' }];
  }

  return [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' }
  ];
};

/**
 * Generate run frequency options based on current form state
 * @param {string} runFrequency - Current run frequency value
 * @returns {Array<{label: string, value: string}>} Run frequency options
 */
export const generateRunFrequencyOptions = (runFrequency) => {
  const baseOptions = [
    { label: 'Daily', value: 'Daily' },
    {
      label: `Weekly on ${getCurrentDayOfWeek()}`,
      value: `Weekly on ${getCurrentDayOfWeek()}`
    },
    {
      label: `Monthly on the ${getCurrentDayOfMonth()}`,
      value: `Monthly on the ${getCurrentDayOfMonth()}`
    }
  ];

  const isCustomValue =
    runFrequency !== 'Custom' &&
    !baseOptions.some((opt) => opt.value === runFrequency);

  if (isCustomValue && runFrequency) {
    baseOptions.push({
      label: runFrequency,
      value: runFrequency
    });
  }

  baseOptions.push({ label: 'Custom', value: 'Custom' });

  return baseOptions;
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
export const validateEmail = (email) => {
  if (!email || email.length > 254) {
    return false;
  }
  const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;

  return emailRegex.test(email);
};

/**
 * Build parameters object for form submission
 * @param {Object} formData - Form data object
 * @returns {Object} Parameters object for API request
 */
export const buildSubmitParameters = (formData) => {
  let parameters = {
    target: formData.targetSystem,
    method: formData.authMethod
  };

  if (formData.authMethod === 'iam-access-key') {
    parameters = {
      ...parameters,
      accessKeyId: formData.accessKeyId,
      secretAccessKey: formData.secretAccessKey,
      awsRegion: formData.awsRegion
    };
  } else if (formData.authMethod === 'oauth') {
    parameters = {
      ...parameters,
      clientId: formData.clientId,
      clientSecret: formData.clientSecret,
      authorizationServerURL: formData.authorizationServerURL,
      tokenEndpointURL: formData.tokenEndpointURL,
      redirectURI: formData.redirectURI,
      scope: formData.scope
    };
  }

  return parameters;
};

/**
 * Validate form based on current form data
 * @param {Object} formData - Form data object
 * @returns {boolean} True if form is valid
 */
export const validateForm = (formData) => {
  const hasName = formData.name?.trim() !== '';
  const hasTargetSystem = formData.targetSystem !== '';
  const hasAuthMethod = formData.authMethod !== '';

  let authFieldsValid = true;

  if (formData.authMethod === 'iam-access-key') {
    authFieldsValid =
      (formData.accessKeyId?.trim() || '') !== '' &&
      (formData.secretAccessKey?.trim() || '') !== '' &&
      (formData.awsRegion || '') !== '';
  } else if (formData.authMethod === 'oauth') {
    authFieldsValid =
      (formData.clientId?.trim() || '') !== '' &&
      (formData.clientSecret?.trim() || '') !== '' &&
      (formData.authorizationServerURL?.trim() || '') !== '' &&
      (formData.tokenEndpointURL?.trim() || '') !== '' &&
      (formData.redirectURI?.trim() || '') !== '';
  }

  return hasName && hasTargetSystem && hasAuthMethod && authFieldsValid;
};
